<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Gasto' : 'Novo Gasto' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label class="form-label">Descrição</label>
          <input v-model="gasto.descricao" type="text" required placeholder="Ex: Conta de Luz" class="form-input" />
        </div>

        <div class="relative" ref="categoryDropdownContainer">
          <label class="form-label">Categoria</label>
          <button @click="isCategoryDropdownOpen = !isCategoryDropdownOpen" type="button" class="form-input flex items-center justify-between text-left">
            <span v-if="gasto.categoria" class="flex items-center gap-2">
              <span class="material-symbols-outlined text-lg">{{ getCategoryIcon(gasto.categoria) }}</span>
              <span>{{ gasto.categoria }}</span>
            </span>
            <span v-else class="text-slate-400 dark:text-slate-500">Selecione uma categoria</span>
            <span class="material-symbols-outlined text-slate-400">unfold_more</span>
          </button>
          <transition name="fade-fast">
            <div v-if="isCategoryDropdownOpen" class="absolute z-20 w-full mt-1 bg-white dark:bg-slate-900 rounded-lg shadow-lg border dark:border-slate-700 max-h-56 overflow-y-auto">
              <ul>
                <li v-for="(icon, name) in categoryIcons" :key="name" @click="selectCategory(name)" class="px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-3">
                  <span class="material-symbols-outlined text-lg">{{ icon }}</span>
                  <span>{{ name }}</span>
                </li>
              </ul>
            </div>
          </transition>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label class="form-label">Valor (R$)</label>
            <input v-model.number="gasto.valor" type="number" step="0.01" required placeholder="0.00" class="form-input" />
          </div>
          <div>
            <label class="form-label">Data de Vencimento</label>
            <input v-model="gasto.dataPagamento" type="date" required class="form-input" />
          </div>
        </div>
        <div>
          <label class="form-label">Status</label>
          <select v-model="gasto.status" required class="form-select">
            <option value="PENDENTE">Pendente</option>
            <option value="PAGO">Pago</option>
            <option value="CONGELADO">Congelado</option>
          </select>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const route = useRoute()

const isEditing = computed(() => !!route.params.id)
const isSubmitting = ref(false)
const getInitialGasto = () => ({
  id: null, descricao: '', valor: null, dataPagamento: new Date().toISOString().split('T')[0],
  status: 'PENDENTE', categoria: 'Padrão', dataQuitacaoReal: null
})
const gasto = ref(getInitialGasto())

const apiBaseUrl = 'https://backend.controlease.com.br';
const apiUrl = `${apiBaseUrl}/gastos`;
const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const categoryIcons = {
  'Alimentação': 'restaurant', 'Transporte': 'commute', 'Moradia': 'home',
  'Lazer': 'sports_esports', 'Saúde': 'health_and_safety', 'Educação': 'school',
  'Compras': 'shopping_bag', 'Serviços': 'dns', 'Viagem': 'flight', 'Padrão': 'receipt_long'
};
const getCategoryIcon = (category) => categoryIcons[category] || categoryIcons['Padrão'];

const isCategoryDropdownOpen = ref(false);
const categoryDropdownContainer = ref(null);
const selectCategory = (name) => {
  gasto.value.categoria = name;
  isCategoryDropdownOpen.value = false;
};

const handleClickOutside = (event) => {
  if (categoryDropdownContainer.value && !categoryDropdownContainer.value.contains(event.target)) {
    isCategoryDropdownOpen.value = false;
  }
};

watch(isCategoryDropdownOpen, (isOpen) => {
  if (isOpen) document.addEventListener('click', handleClickOutside);
  else document.removeEventListener('click', handleClickOutside);
});

const fetchGasto = async () => {
  if (!isEditing.value) return
  try {
    const { data } = await axios.get(`${apiUrl}/${route.params.id}`, authConfig())
    gasto.value = data
  } catch {
    alert('Erro ao carregar gasto.')
    router.push('/user/gastos')
  }
}

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${gasto.value.id}`, gasto.value, authConfig())
    } else {
      await axios.post(apiUrl, gasto.value, authConfig())
    }
    router.push('/user/gastos')
  } catch {
    alert('Não foi possível salvar o gasto.')
  } finally {
    isSubmitting.value = false;
  }
}

const goBack = () => {
  router.go(-1)
}

onMounted(() => {
  if (isEditing.value) {
    fetchGasto()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20;
}
.btn {
  @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60;
}
.btn-primary {
  @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20;
}
.btn-secondary {
  @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm;
}
.form-input,
.form-select {
  @apply w-full px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-800/70 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] focus:bg-white dark:focus:bg-slate-900;
}
.form-select { @apply appearance-none; }
.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5;
}
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.fade-fast-enter-active, .fade-fast-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.fade-fast-enter-from, .fade-fast-leave-to { opacity: 0; transform: scale(0.98) translateY(-4px); }
</style>